import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box, Dialog } from '@mui/material';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getVPAction,
  getRMAction,
  getPMAction,
  deleteUsersAction,
} from 'store/actions/userActions';
import { openDetails } from 'store/actions/uiActions';
import { UserTable } from 'components/Tables/UserTable';
import { getUserTableConfigForRoute } from 'components/Tables/UserTable/userTableConfig';
import { useNavigate } from 'react-router-dom';
import { failedNotif } from 'store/actions/notifActions';

import { DatePickerInput, Dropdown, colors, Button } from '@leapeasy/ui-kit';
import { GlobalUXState } from 'components/GlobalUXState';
import { DetailPageLoadingCenter } from 'components/DetailPageLoadingCenter';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { getPM, getRM, getVP } from 'store/sagas/APIs/user';
import { fetchAllPaginated } from 'utils/exportPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildUserByRoleListFilterChips,
  parseUserByRoleListFilterChipId,
} from 'utils/listTableActiveFilterChips';

// Images
import Img_1 from 'assets/img_1.png';

const parseUserByRoleFilters = (searchParams, _paramKey, fallback) => ({
  selectedBuilings:
    searchParams.get('propertyCount') || searchParams.get('buildings')
      ? (searchParams.get('propertyCount') || searchParams.get('buildings'))
          .split(',')
          .filter(Boolean)
      : fallback.selectedBuilings,
  selectedJobTitle: searchParams.get('jobTitle')
    ? searchParams.get('jobTitle').split(',').filter(Boolean)
    : fallback.selectedJobTitle,
  selectedInvitedDate: searchParams.get('invitedDate') || fallback.selectedInvitedDate || '',
});

const serializeUserByRoleFilters = (filters) => ({
  propertyCount:
    Array.isArray(filters.selectedBuilings) && filters.selectedBuilings.length > 0
      ? filters.selectedBuilings.join(',')
      : '',
  jobTitle:
    Array.isArray(filters.selectedJobTitle) && filters.selectedJobTitle.length > 0
      ? filters.selectedJobTitle.join(',')
      : '',
  invitedDate: filters.selectedInvitedDate || '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const ROLE_LIST_ERROR_KEY = {
  vp: 'vp_list_error',
  rm: 'rm_list_error',
  pm: 'pm_list_error',
};

export const UserByRole = ({ role, title, route }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const users = useSelector((state) => state.getIn(['user', role]));
  const listError = useSelector((state) => state.getIn(['user', ROLE_LIST_ERROR_KEY[role]]));

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: ['landlord_id', 'accepter_email'],
      searchText: '',
      searchPlaceholder: 'Search User...',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: {
      selectedBuilings: [],
      selectedJobTitle: [],
      selectedInvitedDate: '',
    },
    parseExtraFilters: parseUserByRoleFilters,
    serializeExtraFilters: serializeUserByRoleFilters,
  });

  // Dropdown options
  const [selectedBuilings, setSelectedBuildings] = useState([]);
  const [selectedJobTitle, setSelectedJobTitle] = useState([]);
  const [selectedInvitedDate, setSelectedInvitedDate] = useState('');
  const [selectedBuilingsDraft, setSelectedBuildingsDraft] = useState([]);
  const [selectedJobTitleDraft, setSelectedJobTitleDraft] = useState([]);
  const [selectedInvitedDateDraft, setSelectedInvitedDateDraft] = useState('');
  const [selectedUser, setSelecteduser] = useState(null);
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);

  const roleFetchers = {
    vp: getVP,
    rm: getRM,
    pm: getPM,
  };

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedBuildings = Array.isArray(extraFilters.selectedBuilings)
      ? extraFilters.selectedBuilings
      : [];
    const nextSelectedJobTitle = Array.isArray(extraFilters.selectedJobTitle)
      ? extraFilters.selectedJobTitle
      : [];
    const nextSelectedInvitedDate = extraFilters.selectedInvitedDate || '';
    setSelectedBuildings((prev) =>
      areArraysEqual(prev, nextSelectedBuildings) ? prev : nextSelectedBuildings,
    );
    setSelectedJobTitle((prev) =>
      areArraysEqual(prev, nextSelectedJobTitle) ? prev : nextSelectedJobTitle,
    );
    setSelectedInvitedDate((prev) =>
      prev === nextSelectedInvitedDate ? prev : nextSelectedInvitedDate,
    );
    setSelectedBuildingsDraft((prev) =>
      areArraysEqual(prev, nextSelectedBuildings) ? prev : nextSelectedBuildings,
    );
    setSelectedJobTitleDraft((prev) =>
      areArraysEqual(prev, nextSelectedJobTitle) ? prev : nextSelectedJobTitle,
    );
    setSelectedInvitedDateDraft((prev) =>
      prev === nextSelectedInvitedDate ? prev : nextSelectedInvitedDate,
    );
  }, [extraFilters]);

  const [numOfBuildings, setNumOfBuildings] = useState([]);
  const [listOfJobTitle, setListOfJobTitle] = useState([]);
  const usersJSON = users && users.toJS();

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: usersJSON?.total,
    isLoading: !usersJSON,
  });

  useEffect(() => {
    const fetcher = roleFetchers[role];
    if (!fetcher) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetcher({
          filter: {
            options: ['landlord_id', 'accepter_email'],
            searchText: '',
            searchPlaceholder: 'Search User...',
            selectedBuilings: [],
            selectedJobTitle: [],
            selectedInvitedDate: '',
          },
          offset: 0,
          limit: 1,
          sortOptions,
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [role, sortOptions]);

  const handleRequestExportData = useCallback(async () => {
    const fetcher = roleFetchers[role];
    const totalRecords = Number(usersJSON?.total) || 0;
    if (!fetcher || totalRecords === 0) return [];
    try {
      const responseRows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          fetcher({
            filter: {
              ...filter,
              selectedBuilings,
              selectedJobTitle,
              selectedInvitedDate,
            },
            offset,
            limit,
            sortOptions,
          }),
      });
      return responseRows;
    } catch (e) {
      throw new Error('Unable to export filtered users right now.');
    }
  }, [
    role,
    usersJSON?.total,
    filter,
    selectedBuilings,
    selectedJobTitle,
    selectedInvitedDate,
    sortOptions,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const fetcher = roleFetchers[role];
    const totalRecords = Number(fullExportDatasetTotal) || Number(usersJSON?.total) || 0;
    if (!fetcher || totalRecords === 0) return [];
    try {
      const responseRows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          fetcher({
            filter: {
              options: ['landlord_id', 'accepter_email'],
              searchText: '',
              searchPlaceholder: 'Search User...',
              selectedBuilings: [],
              selectedJobTitle: [],
              selectedInvitedDate: '',
            },
            offset,
            limit,
            sortOptions,
          }),
      });
      return responseRows;
    } catch (e) {
      throw new Error('Unable to export all users right now.');
    }
  }, [role, fullExportDatasetTotal, usersJSON?.total, sortOptions]);

  const handleFetchList = useCallback(() => {
    const query = {
      filter: {
        ...filter,
        selectedBuilings,
        selectedJobTitle,
        selectedInvitedDate,
      },
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions,
    };
    switch (role) {
      case 'vp':
        dispatch(getVPAction(query));
        break;
      case 'rm':
        dispatch(getRMAction(query));
        break;
      case 'pm':
        dispatch(getPMAction(query));
        break;
      default:
        break;
    }
  }, [
    dispatch,
    role,
    filter,
    selectedBuilings,
    selectedJobTitle,
    selectedInvitedDate,
    pagination,
    sortOptions,
  ]);

  useEffect(() => {
    handleFetchList();
  }, [handleFetchList]);

  const filterFunc = () => {
    if (!users) return;
    // List numbers of buildings
    let tmp_num_buildings = [];
    let tmp_list_job_title = [];
    for (let i = 0; i < usersJSON.data.length; i++) {
      tmp_num_buildings.filter(
        (building, _) => building.value === usersJSON.data[i].property.length,
      ).length === 0 &&
        tmp_num_buildings.push({
          text: usersJSON.data[i].property.length,
          value: usersJSON.data[i].property.length,
        });
      tmp_list_job_title.filter(
        (building, _) => building.value === usersJSON.data[i].accepter.job_title,
      ).length === 0 &&
        tmp_list_job_title.push({
          text: usersJSON.data[i].accepter.job_title,
          value: usersJSON.data[i].accepter.job_title,
        });
    }
    tmp_num_buildings.sort(function (a, b) {
      return a.value - b.value;
    });
    tmp_list_job_title.sort(function (a, b) {
      return a.value - b.value;
    });
    setNumOfBuildings([...tmp_num_buildings]);
    setListOfJobTitle([...tmp_list_job_title]);
  };

  useEffect(() => {
    filterFunc();
  }, [users]);
  const onClickUser = useCallback(
    (user) => {
      const delegationId = user?.id;
      const shouldUseParamRoute =
        route === 'full-portfolio' || route === 'multi-site' || route === 'property';
      if (shouldUseParamRoute && !delegationId) {
        dispatch(failedNotif('User detail ID is missing.'));
        return;
      }
      dispatch(
        openDetails({
          type: 'user',
          data: user,
        }),
      );
      if (shouldUseParamRoute) {
        navigate(`/user/${route}/${delegationId}/detail`);
        return;
      }
      navigate(`/user/${route}/detail`);
    },
    [dispatch, navigate, route],
  );

  const onClickEdit = useCallback(
    (user) => {
      const delegationId = user?.id;
      if (!delegationId) {
        dispatch(failedNotif('User edit ID is missing.'));
        return;
      }
      dispatch(
        openDetails({
          type: 'user',
          data: user,
        }),
      );
      navigate(`/user/${route}/${delegationId}/edit`);
    },
    [dispatch, navigate, route],
  );

  const applyFunc = useCallback(() => {
    setSelectedBuildings(selectedBuilingsDraft);
    setSelectedJobTitle(selectedJobTitleDraft);
    setSelectedInvitedDate(selectedInvitedDateDraft);
    setExtraFilters({
      selectedBuilings: selectedBuilingsDraft,
      selectedJobTitle: selectedJobTitleDraft,
      selectedInvitedDate: selectedInvitedDateDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedBuilingsDraft,
    selectedJobTitleDraft,
    selectedInvitedDateDraft,
    setExtraFilters,
    setPagination,
  ]);

  const getUserListQuery = useCallback(
    () => ({
      filter: {
        ...filter,
        selectedBuilings,
        selectedJobTitle,
        selectedInvitedDate,
      },
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions,
    }),
    [filter, selectedBuilings, selectedJobTitle, selectedInvitedDate, pagination, sortOptions],
  );

  const handleDelete = useCallback(() => {
    if (selectedUser == null) return;

    const row = typeof selectedUser === 'object' ? selectedUser : usersJSON?.data?.[selectedUser];

    if (!row) {
      dispatch(failedNotif('Unable to delete user. Please try again.'));
      setSelecteduser(null);
      return;
    }

    dispatch(
      deleteUsersAction({
        data: row,
        role,
        listQuery: getUserListQuery(),
      }),
    );
    setSelecteduser(null);
  }, [selectedUser, usersJSON?.data, dispatch, role, getUserListQuery]);

  const resetFunc = useCallback(() => {
    setSelectedBuildings([]);
    setSelectedJobTitle([]);
    setSelectedInvitedDate('');
    setSelectedBuildingsDraft([]);
    setSelectedJobTitleDraft([]);
    setSelectedInvitedDateDraft('');
    setExtraFilters({
      selectedBuilings: [],
      selectedJobTitle: [],
      selectedInvitedDate: '',
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const listFilterChips = useMemo(
    () =>
      buildUserByRoleListFilterChips({
        selectedBuilings,
        selectedJobTitle,
        selectedInvitedDate,
        jobTitleOptions: listOfJobTitle,
      }),
    [selectedBuilings, selectedJobTitle, selectedInvitedDate, listOfJobTitle],
  );

  const handleRemoveUserByRoleListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseUserByRoleListFilterChipId(chipId);
      if (!parsed) return;
      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      if (parsed.type === 'invitedDate') {
        setSelectedInvitedDate('');
        setSelectedInvitedDateDraft('');
        setExtraFilters((prev) => ({ ...prev, selectedInvitedDate: '' }));
        bumpPage();
        return;
      }
      if (parsed.type === 'propertyCount') {
        const next = selectedBuilings.filter((s) => s !== parsed.value);
        setSelectedBuildings(next);
        setSelectedBuildingsDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedBuilings: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'jobTitle') {
        const next = selectedJobTitle.filter((s) => s !== parsed.value);
        setSelectedJobTitle(next);
        setSelectedJobTitleDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedJobTitle: next }));
        bumpPage();
      }
    },
    [selectedBuilings, selectedJobTitle, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveUserByRoleListFilterChip,
    resetFunc,
  );

  const userTableConfig = useMemo(() => getUserTableConfigForRoute(route), [route]);

  return (
    <DashboardLayoutContainer>
      {listError ? (
        <GlobalUXState variant="apiError" actionLabel="Retry" onAction={handleFetchList} />
      ) : users ? (
        <Box>
          <Dialog open={!!selectedUser}>
            <DialogContainer col1={colors.purple[900]} col2={colors.neutral[900]}>
              <img src={Img_1} />
              <p>Are you sure you want to delete this user?</p>
              <p>This will remove their access to the Leap portal.</p>
              <div>
                <Button
                  size="medium"
                  variant="tertiary"
                  style={{ flexShrink: 2 }}
                  onClick={() => setSelecteduser(null)}
                >
                  Discard
                </Button>

                <Button
                  size="medium"
                  variant="primary"
                  style={{ flexShrink: 2 }}
                  onClick={handleDelete}
                >
                  Yes, Delete
                </Button>
              </div>
            </DialogContainer>
          </Dialog>
          <UserTable
            title={title}
            role={role}
            users={usersJSON.data}
            filter={filter}
            setSelecteduser={setSelecteduser}
            defaultVisibleColumnKeys={userTableConfig.defaultVisibleColumnKeys}
            columnVisibilityStorageKey={userTableConfig.columnVisibilityStorageKey}
            dropFilterActiveSummary={dropFilterActiveSummary}
            dropFilterActiveCount={dropFilterActiveCount}
            dropFilter={{
              component: (
                <DropFilterContainer>
                  <Dropdown
                    onChange={(e) => {
                      setSelectedBuildingsDraft(e.target.value);
                    }}
                    options={numOfBuildings}
                    value={selectedBuilingsDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder=""
                    placeholderFlag={false}
                    label="Property Count"
                    style={{
                      width: '100%',
                    }}
                  />
                  <Dropdown
                    onChange={(e) => {
                      setSelectedJobTitleDraft(e.target.value);
                    }}
                    options={listOfJobTitle}
                    value={selectedJobTitleDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder=""
                    placeholderFlag={true}
                    label="Job Title"
                    style={{
                      width: '100%',
                    }}
                  />
                  <RangeDatePickerContainer>
                    <DatePickerInput
                      label={'Invited Date'}
                      size="small"
                      value={selectedInvitedDateDraft}
                      onChange={(date) => {
                        setSelectedInvitedDateDraft(date.target.value);
                      }}
                    />
                  </RangeDatePickerContainer>
                </DropFilterContainer>
              ),
              applyFunc: applyFunc,
              resetFunc: resetFunc,
            }}
            onChangeFilter={setFilter}
            pagination={{
              ...pagination,
              totalItems: usersJSON.total || usersJSON.data.length,
              totalPages: Math.ceil(
                (usersJSON.total || usersJSON.data.length) / pagination.rowsPerPage,
              ),
            }}
            onChangePagination={setPagination}
            sortOptions={sortOptions}
            onChangeSort={setSortOptions}
            onClickUser={onClickUser}
            onClickEdit={onClickEdit}
            onRequestExportData={handleRequestExportData}
            onRequestFullExportData={handleRequestFullExportData}
            exportFullDatasetTotal={fullExportDatasetTotal}
          />
        </Box>
      ) : (
        <DetailPageLoadingCenter label={`Loading ${title}...`} />
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const RangeDatePickerContainer = styled.div`
  display: flex;
  max-width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  * {
    box-sizing: border-box;
  }
  & > div {
    width: 100% !important;
  }

  .ui-kit-input-body {
    width: 278px;
  }
`;

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-radius: 8px;
  width: 350px;
  text-align: center;
  box-sizing: border-box;
  * {
    box-sizing: border-box;
  }
  & > p:nth-of-type(1) {
    color: ${(props) => props.col1};
    font-size: 14px;
    font-weight: 600;
  }

  & > p:nth-of-type(2) {
    color: ${(props) => props.col2};
    font-size: 12px;
    font-weight: 400;
  }

  & > div {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    width: 100%;

    button {
      flex: 1;
    }
  }
`;
